import { connect } from 'react-redux';
import { withStyles } from '@mui/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Tooltip from '@mui/material/Tooltip';
import QuoteWizardState from '../../../store/QuoteWizardState';
import { WithStyles, createStyles } from '@mui/styles';

// for displaying the forms and copying the string representation
import {
  generateQuoteDisplayStringFromAnswers,
  copyTextToClipboard,
} from './utility';
import { Dispatch, StoreState } from '../../../store';
import { Theme } from '@mui/system';

interface BaseIncompleteQuoteSubmissionErrorProps
  extends WithStyles<typeof styles>,
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {}

const BaseIncompleteQuoteSubmissionError = ({
  error,
  clearError,
  cancelAllSubmissions,
  formAnswers,
  businessName,
  classes,
}: BaseIncompleteQuoteSubmissionErrorProps) => {
  const displayString = `${
    window.location.host
  }\n\n${generateQuoteDisplayStringFromAnswers(formAnswers, businessName)}`;

  const handleClose = () => {
    cancelAllSubmissions();
    clearError();
  };

  return (
    <Dialog open={!!error} onClose={handleClose}>
      <Typography variant='h5' className={classes.title}>
        Error Saving Form
      </Typography>
      <div className={classes.outerContainer}>
        <Typography variant='body2'>
          We're very sorry, but there was an error saving your application.
        </Typography>
        <Typography variant='body2'>
          Luckily, we've managed to keep your answers. Please send the questions
          and responses to{' '}
          <a
            href={`mailto:support@calefy.ca?subject=Error Saving Incomplete Quote&body=${displayString.replace(
              /\n/g,
              '%0D%0A',
            )}`}
            target={'_blank'}
            rel='noopener noreferrer'
          >
            support@calefy.ca
          </a>
          . If that link doesn't work for you, please copy the following into
          your email:
        </Typography>
        <div className={classes.copyIconContainer}>
          <Tooltip title='Copy to Clipboard' aria-label='copy to clipboard'>
            <IconButton
              size='small'
              onClick={() => copyTextToClipboard(displayString)}
            >
              <FileCopyIcon className={classes.copyIcon} />
            </IconButton>
          </Tooltip>
        </div>
        <pre className={classes.answers}>{displayString}</pre>
        <Typography variant='body2'>
          Once again, our apologies for this inconvenience. We'll get back to
          you within 2 - 3 business days.
        </Typography>
        <DialogActions>
          <Button variant='contained' onClick={handleClose}>
            Return
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    error: state.quoteWizard.errors.incompleteQuoteSubmission,
    formAnswers: state.quoteWizard.formAnswers,
    businessName: state.quoteWizard.selectedBusinessType?.displayName || '',
  };
};
const mapDispatchToProps = (dispatch: Dispatch) => {
  const { updateErrors, cancelAllSubmissions } = QuoteWizardState.actions;

  return {
    clearError: () =>
      dispatch(updateErrors({ incompleteQuoteSubmission: null })),
    cancelAllSubmissions: () => dispatch(cancelAllSubmissions()),
  };
};
const ConnectedIncompleteQuoteSubmissionError = connect(
  mapStateToProps,
  mapDispatchToProps,
)(BaseIncompleteQuoteSubmissionError);

const styles = (theme: Theme) =>
  createStyles({
    title: {
      color: 'white',
      textAlign: 'center',
      backgroundColor: theme.palette.grey[700],
      padding: `${theme.spacing(2)} 0`,
    },
    outerContainer: {
      maxWidth: '600px',
      padding: theme.spacing(2),
    },
    answers: {
      maxHeight: '500px',
      overflowY: 'scroll',
    },
    copyIcon: {},
    downloadIcon: {
      color: theme.palette.primary.main,
    },
    copyIconContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
  });

export const IncompleteQuoteSubmissionError = withStyles(styles)(
  ConnectedIncompleteQuoteSubmissionError,
);
